import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';

import { GridListItem, type ItemLayoutProps } from 'app/main/components/GridList';
import { ButtonLink, FormattedDateTimeRange, ImageAvatar, SquareChip, Text, Utils } from 'app/main/components';
import { useSettings } from 'app/main/components/hooks';
import buttons from './ErpOrdersButtons';

export default function ErpOrdersListItem(props: ItemLayoutProps) {
	const { t } = useSettings();

	const avatar = (
		<ImageAvatar
			dimensions={44}
			module="companies"
			uid={props.model.ords_comp_uid}
			avatar={props.model.comp_avatar}
			initials={props.model.comp_title_initials}
		/>
	);

	return (
		<GridListItem avatar={avatar} buttons={buttons.view?.itemButtons}>
			<Grid container justifyContent="space-between" spacing={1}>
				<Grid size={props.width >= 600 ? 6 : 12}>
					<Grid container>
						<Grid size={12}>
							{/* TITLE */}
							<Text
								className="font-600"
								variant="subtitle2"
								to={`/erp/orders/details/${props.model.ords_uid}`}
							>
								{props.model.ords_comp_title && `${props.model.ords_comp_title} - `}
								{props.model.ords_erp_id}
							</Text>
						</Grid>
						<Grid>
							{/* ORDER DATE - REQUESTED DELIVERY DATE */}
							<SquareChip
								label={
									props.model.ords_d_order || props.model.ords_d_delivery_requested ? (
										<FormattedDateTimeRange
											from={props.model.ords_d_order}
											to={props.model.ords_d_delivery_requested}
										/>
									) : (
										t('VIEW_ITEM_NO_DATES')
									)
								}
								disabled={!props.model.ords_d_order && !props.model.ords_d_delivery_requested}
							/>
						</Grid>
						<Grid>
							{/* TYPE */}
							{props.model.orty_title && (
								<SquareChip label={props.model.orty_title} backgroundColor={props.model.orty_color} />
							)}
						</Grid>
						<Grid>
							{/* TOT TAX EXCL */}
							<SquareChip
								label={
									<FormattedMessage
										id="ordersListItem.totTaxExcl"
										defaultMessage={t('TOT_VALUE_TAX_EXCLUDED')}
										values={{
											value: props.model.ords_tot_value_tax_excl
												? Utils.formatCurrency(props.model.ords_tot_value_tax_excl)
												: ' N/A'
										}}
									/>
								}
								disabled={!props.model.ords_tot_value_tax_excl}
							/>
						</Grid>
						<Grid>
							{/* TOT TAX INCL */}
							<SquareChip
								label={
									<FormattedMessage
										id="ordersListItem.totTaxIncl"
										defaultMessage={t('TOT_VALUE_TAX_INCLUDED')}
										values={{
											value: props.model.ords_tot_value_tax_incl
												? Utils.formatCurrency(props.model.ords_tot_value_tax_incl)
												: ' N/A'
										}}
									/>
								}
								disabled={!props.model.ords_tot_value_tax_incl}
							/>
						</Grid>
					</Grid>
				</Grid>
				{props.width >= 600 && (
					<Grid size={props.width >= 960 ? 3 : 6}>
						<Grid container>
							<Grid size={12}>
								{/* INTERNAL ASSIGNED USER */}
								<ButtonLink
									href={
										props.model.ords_user_assigned_uid &&
										`/users/details/${props.model.ords_user_assigned_uid}`
									}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_INTUSER')}
								>
									{props.model.ords_user_assigned_title}
								</ButtonLink>
							</Grid>
							<Grid size={12}>
								{/* EXTERNAL ASSIGNED USER */}
								<ButtonLink
									href={
										props.model.ords_user_salesperson_uid &&
										`/users/details/${props.model.ords_user_salesperson_uid}`
									}
									icon="account-circle"
									fallback={t('VIEW_ITEM_NO_EXTUSER')}
								>
									{props.model.ords_user_salesperson_title}
								</ButtonLink>
							</Grid>
						</Grid>
					</Grid>
				)}
				{props.width >= 960 && (
					<Grid size={3}>
						<Grid container>
							<Grid>
								{/* PAYMENT TERMS */}
								{props.model.pate_title && (
									<SquareChip
										label={props.model.pate_title}
										backgroundColor={props.model.pate_color}
									/>
								)}
							</Grid>
							<Grid>
								{/* PAYMENT METHOD */}
								{props.model.pame_title && (
									<SquareChip
										label={props.model.pame_title}
										backgroundColor={props.model.pame_color}
									/>
								)}
							</Grid>
							<Grid>
								{/* SHIPPING METHOD */}
								{props.model.shme_title && (
									<SquareChip
										label={props.model.shme_title}
										backgroundColor={props.model.shme_color}
									/>
								)}
							</Grid>
							<Grid>
								{/* SHIPPING AGENT */}
								{props.model.shag_title && (
									<SquareChip
										label={props.model.shag_title}
										backgroundColor={props.model.shag_color}
									/>
								)}
							</Grid>
						</Grid>
					</Grid>
				)}
			</Grid>
		</GridListItem>
	);
}
